import { Link } from "react-router-dom";
import { createPortal } from "react-dom";
import { 
  Sparkles, 
  Shirt, 
  Gem, 
  ShoppingBag, 
  Home, 
  Droplet,
  Heart,
  Flame
} from "lucide-react";
import { Z_INDEX } from "@/constants/ui.constants";

interface MegaMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

// Reusable className strings for consistent keyboard navigation
const SECTION_LINK_CLASS = "flex items-center gap-2 pb-2 border-b border-border group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm";
const NAV_LINK_CLASS = "block text-muted-foreground hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm px-2 py-1";

export function MegaMenu({ isOpen, onClose, onMouseEnter, onMouseLeave }: MegaMenuProps) {
  if (!isOpen) return null;

  // Keyboard navigation: Close on Escape
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  return createPortal(
    <>
      {/* Backdrop overlay - only covers content below navbar */}
      <div 
        className="fixed left-0 right-0 bottom-0 top-20 bg-background/20 backdrop-blur-sm" 
        style={{ zIndex: Z_INDEX.MEGA_MENU - 1 }}
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Menu container - independent floating */}
      <div 
        className="fixed left-0 right-0 top-20"
        style={{ zIndex: Z_INDEX.MEGA_MENU }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onKeyDown={handleKeyDown}
        role="dialog"
        aria-label="Collections menu"
        aria-modal="false"
      >
        {/* Menu panel */}
        <div className="mx-auto px-4 w-full max-w-[90vw]">
        <div className="bg-card border border-border rounded-lg shadow-lg p-8 w-full">
          <div className="grid grid-cols-3 gap-12">
            
            {/* Left Column: Jewelry & Accessories */}
            <div className="space-y-4">
              {/* Jewelry Section */}
              <div className="space-y-3">
                <Link 
                  to="/collections/jewelry" 
                  className={SECTION_LINK_CLASS}
                  onClick={onClose}
                >
                  <Gem className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">Jewelry</h3>
                </Link>
                <nav className="space-y-0">
                  <Link 
                    to="/collections/jewelry?filter=necklaces" 
                    className={NAV_LINK_CLASS}
                    onClick={onClose}
                  >
                    Necklaces
                  </Link>
                  <Link 
                    to="/collections/jewelry?filter=bracelets" 
                    className={NAV_LINK_CLASS}
                    onClick={onClose}
                  >
                    Bracelets
                  </Link>
                  <Link 
                    to="/collections/jewelry?filter=rings" 
                    className={NAV_LINK_CLASS}
                    onClick={onClose}
                  >
                    Rings
                  </Link>
                  <Link 
                    to="/collections/jewelry?filter=earrings" 
                    className={NAV_LINK_CLASS}
                    onClick={onClose}
                  >
                    Earrings
                  </Link>
                  <Link 
                    to="/collections/jewelry?filter=jewelry%20sets" 
                    className={NAV_LINK_CLASS}
                    onClick={onClose}
                  >
                    Jewelry Sets
                  </Link>
                </nav>
              </div>

              {/* Accessories Section */}
              <div className="space-y-3">
                <Link 
                  to="/collections/accessories" 
                  className={SECTION_LINK_CLASS}
                  onClick={onClose}
                >
                  <ShoppingBag className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">Accessories</h3>
                </Link>
                <nav className="space-y-0">
                  <Link 
                    to="/collections/bags" 
                    className={NAV_LINK_CLASS}
                    onClick={onClose}
                  >
                    Bags
                  </Link>
                  <Link 
                    to="/collections/shoes" 
                    className={NAV_LINK_CLASS}
                    onClick={onClose}
                  >
                    Shoes
                  </Link>
                  <Link 
                    to="/collections/accessories?filter=hats" 
                    className={NAV_LINK_CLASS}
                    onClick={onClose}
                  >
                    Hats & Scarves
                  </Link>
                  <Link 
                    to="/collections/accessories?filter=belts" 
                    className={NAV_LINK_CLASS}
                    onClick={onClose}
                  >
                    Belts
                  </Link>
                </nav>
              </div>
            </div>

            {/* Center Column: Apparel & Special Collections */}
            <div className="space-y-4">
              {/* Apparel Section */}
              <div className="space-y-3">
                <Link
                  to="/collections/apparel" 
                  className={SECTION_LINK_CLASS}
                  onClick={onClose}
                >
                  <Shirt className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">Apparel</h3>
                </Link>
                <nav className="space-y-0">
                  <Link 
                    to="/collections/tops" 
                    className={NAV_LINK_CLASS}
                    onClick={onClose}
                  >
                    Tops
                  </Link>
                  <Link 
                    to="/collections/bottoms" 
                    className={NAV_LINK_CLASS}
                    onClick={onClose}
                  >
                    Bottoms
                  </Link>
                  <Link 
                    to="/collections/dresses" 
                    className={NAV_LINK_CLASS}
                    onClick={onClose}
                  >
                    Dresses
                  </Link>
                  <Link 
                    to="/collections/one-pieces" 
                    className={NAV_LINK_CLASS}
                    onClick={onClose}
                  >
                    One-Pieces
                  </Link>
                  <Link 
                    to="/collections/lounge-wear" 
                    className={NAV_LINK_CLASS}
                    onClick={onClose}
                  >
                    Lounge Wear
                  </Link>
                </nav>
              </div>

              {/* Special Collections Section */}
              <div className="space-y-3">
                <Link 
                  to="/collections" 
                  className={SECTION_LINK_CLASS}
                  onClick={onClose}
                >
                  <Heart className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">Special Collections</h3>
                </Link>
                <nav className="space-y-0">
                  <Link 
                    to="/collections/goddess" 
                    className="block text-muted-foreground hover:text-primary transition-colors group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm px-2 py-1"
                    onClick={onClose}
                  >
                    <span className="flex items-center gap-2">
                      <span>Goddess Collection</span>
                      <Sparkles className="h-4 w-4 text-primary transition-opacity" />
                    </span>
                  </Link>
                  <Link 
                    to="/collections/hot-chocolate-designs" 
                    className={NAV_LINK_CLASS}
                    onClick={onClose}
                  >
                    Hot Chocolate Design 🎨
                  </Link>
                  <Link 
                    to="/collections/chrome-hearts" 
                    className={NAV_LINK_CLASS}
                    onClick={onClose}
                  >
                    Chrome Hearts 🖤
                  </Link>
                  <Link 
                    to="/collections/sale" 
                    className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm px-2 py-1"
                    onClick={onClose}
                  >
                    Sale
                    <Flame className="h-4 w-4 text-orange-500" />
                  </Link>
                </nav>
              </div>
            </div>

            {/* Right Column: Lifestyle & Home, Bath & Body */}
            <div className="space-y-4">
              {/* Lifestyle & Home Section */}
              <div className="space-y-3">
                <Link 
                  to="/collections/lifestyle" 
                  className={SECTION_LINK_CLASS}
                  onClick={onClose}
                >
                  <Home className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">Lifestyle & Home</h3>
                </Link>
                <nav className="space-y-0">
                  <Link 
                    to="/collections/furniture" 
                    className={NAV_LINK_CLASS}
                    onClick={onClose}
                  >
                    Furniture & Seating
                  </Link>
                  <Link 
                    to="/collections/rugs" 
                    className={NAV_LINK_CLASS}
                    onClick={onClose}
                  >
                    Rugs
                  </Link>
                  <Link 
                    to="/collections/textiles" 
                    className={NAV_LINK_CLASS}
                    onClick={onClose}
                  >
                    Linens & Blankets
                  </Link>
                  <Link 
                    to="/collections/decor" 
                    className={NAV_LINK_CLASS}
                    onClick={onClose}
                  >
                    Decor & Accents
                  </Link>
                  <Link 
                    to="/collections/outdoors" 
                    className={NAV_LINK_CLASS}
                    onClick={onClose}
                  >
                    Porch & Patio
                  </Link>
                </nav>
              </div>

              {/* Bath & Body Section */}
              <div className="space-y-3">
                <Link 
                  to="/collections/bath-body" 
                  className={SECTION_LINK_CLASS}
                  onClick={onClose}
                >
                  <Droplet className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">Bath & Body</h3>
                </Link>
                <nav className="space-y-0">
                  <Link 
                    to="/collections/bath-body" 
                    className={NAV_LINK_CLASS}
                    onClick={onClose}
                  >
                    All Bath & Body
                  </Link>
                </nav>
              </div>
            </div>

          </div>

          {/* View All Collections CTA */}
          <div className="mt-6 pt-4 border-t border-border text-center">
            <Link 
              to="/collections" 
              className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-medium transition-colors"
              onClick={onClose}
            >
              View All Collections
              <span aria-hidden="true">→</span>
            </Link>
          </div>
        </div>
        </div>
      </div>
    </>,
    document.body
  );
}
